/**
 * werte-summary — the shared render faces for a person's Workshop-1 »Selbstbild«
 * results: the committed 1·3·4 value pyramid and the four confirmed personal
 * statements. ONE implementation, consumed by the W1 track surfaces and the Kompass
 * »Meine Werte« mirror (career-werte-view) — so every surface that shows a person
 * their values renders the exact same result faces and they can never drift.
 * (The Workshops hub was a consumer until 2026-07-30; it now ROUTES rather than
 * presenting results, so the outcome faces live only where the results belong.)
 *
 * Pure lit-html, no VM: each face takes the resolved `t()` and the already-fetched
 * domain shape. The Schwartz value wheel lives in its own shared module
 * (`app/lib/schwartz-wheel.ts`) and both surfaces import it directly. Faces are flat
 * document entries (§0 »seamless document«) — no bounding boxes here.
 */

import { html, nothing, type TemplateResult } from "@platform/web-kit/render"
import { valueCardById, type ValueCard } from "@career/definition-v2/value-card-catalog"
import type { PersonalStatement, PersonalStatements, ValuePyramid } from "@career/definition-v2"
import type { CareerI18n, CareerKey } from "../../i18n"

type Translate = CareerI18n["t"]

const TIER_LABEL: Record<string, CareerKey> = {
  Lebenswert: "valuecard.tier.lebenswert",
  Superkräfte: "valuecard.tier.superkraefte",
  Basiswerte: "valuecard.tier.basiswerte",
}
const TIER_ORDER = ["Lebenswert", "Superkräfte", "Basiswerte"] as const

/** The four statement kinds, always in canonical order. */
const STATEMENT_ORDER: PersonalStatement["kind"][] = ["warum", "positionierung", "philosophie", "vision"]
const STATEMENT_LABEL: Record<PersonalStatement["kind"], CareerKey> = {
  warum: "sektion.statement.warum",
  positionierung: "sektion.statement.positionierung",
  philosophie: "sektion.statement.philosophie",
  vision: "sektion.statement.vision",
}

/** A single value-card face — colour field + theme-aware illustration + the term.
 *  Mirrors the cardgame's `cardFace`: a missing webp simply doesn't paint. */
export function valueCardFace(card: ValueCard): TemplateResult {
  const media = `--vg-img-l:url('/values/${card.cardId}.light.webp');--vg-img-d:url('/values/${card.cardId}.dark.webp')`
  return html`<span class="vg-card vg-fam-${card.colour}">
    <span class="vg-card-field" aria-hidden="true"></span>
    <span class="vg-card-illus" style=${media} aria-hidden="true"></span>
    <span class="vg-card-term" lang="de">${card.name}</span>
  </span>`
}

/** The committed 1·3·4 value pyramid (Lebenswert · Superkräfte · Basiswerte). */
export function valuePyramid(t: Translate, pyramid: ValuePyramid): TemplateResult {
  const byTier = (tier: string) =>
    pyramid.slots
      .filter((s) => s.tier === tier)
      .map((s) => valueCardById(s.cardId))
      .filter((c): c is ValueCard => Boolean(c))
  const tierRow = (tier: string) => {
    const cards = byTier(tier)
    if (cards.length === 0) return nothing
    return html`<div class="vg-tier">
      <span class="vg-tier-label eyebrow" lang="de">${t(TIER_LABEL[tier] ?? "valuecard.tier.basiswerte")}</span>
      <ul class="vg-tier-cards" role="list">
        ${cards.map((c) => html`<li class="vg-tier-card vg-fam-${c.colour}">${valueCardFace(c)}</li>`)}
      </ul>
    </div>`
  }
  return html`<div class="vg-pyramid werte-pyramid">${TIER_ORDER.map((tier) => tierRow(tier))}</div>`
}

/** The four confirmed personal statements as flat, hairline-separated document
 *  entries (§0). `gapKey` supplies the honest »nothing here yet« line per surface. */
export function valueStatements(t: Translate, statements: PersonalStatements, gapKey: CareerKey): TemplateResult {
  const byKind = new Map(statements.statements.map((s) => [s.kind, s.text]))
  return html`<div class="werte-stmts">
    ${STATEMENT_ORDER.map((k) => {
      const text = byKind.get(k) ?? ""
      return html`<article class="werte-stmt">
        <h3 class="werte-stmt-label eyebrow" lang="de">${t(STATEMENT_LABEL[k])}</h3>
        ${text.trim()
          ? html`<p class="werte-stmt-body" lang="de">${text}</p>`
          : html`<p class="werte-stmt-gap surface-empty" lang="de">${t(gapKey)}</p>`}
      </article>`
    })}
  </div>`
}
